import React from "react";
import type { StudyValidation } from "./types";

/**
 * Parsing state (LU-A10 + LU-A3): a calm "reading headers locally" readout with
 * an indeterminate sweep and a validation checklist. Presentational — values come
 * from the page once dicom-parser has read the headers; until then items pend.
 */
interface ParsingPanelProps {
  fileCount?: number;
  /** Populated incrementally by the logic lane as checks resolve. */
  validation?: StudyValidation | null;
}

interface CheckProps {
  label: string;
  value?: string;
  done: boolean;
}

const Check: React.FC<CheckProps> = ({ label, value, done }) => (
  <div className={`flex items-center gap-3 font-mono text-xs ${done ? "text-fg" : "text-fg/55"}`}>
    <span
      className={`relative h-3.5 w-3.5 flex-none rounded-[2px] border ${
        done ? "border-signal bg-signal" : "border-border-strong"
      }`}
    >
      {done && (
        <span className="absolute left-[4px] top-px h-2 w-1 rotate-45 border-b-[1.6px] border-r-[1.6px] border-fg" />
      )}
    </span>
    {label}
    <span className={`ml-auto ${done ? "text-signal-text-tint" : "text-fg/25"}`}>
      {value ?? "…"}
    </span>
  </div>
);

export const ParsingPanel: React.FC<ParsingPanelProps> = ({ fileCount, validation }) => (
  <div data-testid="upload-parsing">
    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-fg-dim">
      Reading study · headers only
    </p>
    {/* Indeterminate sweep — reuses the global .fetch-sweep motif. */}
    <div className="my-5 h-1 w-full overflow-hidden rounded-[2px] border border-border bg-surface">
      <div className="fetch-sweep h-full w-full" />
    </div>
    <div className="grid gap-2.5">
      <Check
        label="Single StudyInstanceUID"
        done={!!validation?.studyInstanceUid}
        value={validation?.studyInstanceUid ? "1 found" : undefined}
      />
      <Check label="Modality" done={validation?.modality === "CT"} value={validation?.modality} />
      <Check
        label="Instances"
        done={validation != null}
        value={
          validation
            ? `${validation.instanceCount} files`
            : fileCount
              ? `${fileCount} files`
              : undefined
        }
      />
      <Check label="Scanning identifying tags" done={false} />
    </div>
  </div>
);
